import { ICommandBarItemProps, Label, MessageBar, MessageBarType, Panel, PanelType, SearchBox, SelectionMode, Stack, Text } from '@fluentui/react';
import React from 'react';
import { format, subDays } from 'date-fns';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import { useAuthenticationContext } from '../../hooks/useAuthenticationContext';
import { IAuditLog } from '../../models';
import { Role } from '../../utils/Enums';
import ListComponent from '../../components/ListComponent';
import FreezableDetailsList, { IFilterableColumn } from '../../components/FreezableDetailsList';
import appstyles from '../../styles/App.module.scss';

interface IAuditLogProps
{

}

const PERIODS = [
  { days: 7, text: 'Seneste 7 dage' },
  { days: 30, text: 'Seneste 30 dage' },
  { days: 90, text: 'Seneste 90 dage' }
];

const formatTimestamp = (date: Date) : string => format(date, "dd-MM-yyyy HH:mm:ss");

const formatPayload = (payload?: string) : string => {
  if(!payload) return '';

  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

export const AuditLog: React.FunctionComponent<IAuditLogProps> = (props: React.PropsWithChildren<IAuditLogProps>) => {
    const { Services } = React.useContext(ApplicationContext);
    const { isUserInRole } = useAuthenticationContext();
    const [allEntries, setAllEntries] = React.useState<IAuditLog[] | undefined>(undefined);
    const [searchTerm, setSearchTerm] = React.useState<string>('');
    const [visibleCount, setVisibleCount] = React.useState<number>(0);
    const [periodDays, setPeriodDays] = React.useState<number>(30);
    const [selected, setSelected] = React.useState<IAuditLog | undefined>(undefined);
    const [errorText, setErrorText] = React.useState<string>('');
    const isAdministrator = isUserInRole(Role.Administrator);

    const COLUMNS = [
      {
        key: 'created',
        name: 'Tidspunkt',
        fieldName: 'created',
        minWidth: 140,
        maxWidth: 140,
        isResizable: true,
        onRender: (item: IAuditLog) => <span>{formatTimestamp(item.created)}</span>
      },
      {
        key: 'user',
        name: 'Bruger',
        fieldName: 'userName',
        minWidth: 180,
        maxWidth: 220,
        isResizable: true,
        filter: { getValue: (item: IAuditLog) => item.userName ?? item.userEmail },
        onRender: (item: IAuditLog) => <span title={item.userEmail}>{item.userName ?? item.userEmail}</span>
      },
      {
        key: 'guildShortName',
        name: 'Laug',
        fieldName: 'guildShortName',
        minWidth: 80,
        maxWidth: 100,
        isResizable: true,
        filter: true
      },
      {
        key: 'area',
        name: 'Område',
        fieldName: 'area',
        minWidth: 140,
        maxWidth: 160,
        isResizable: true,
        filter: true
      },
      {
        key: 'action',
        name: 'Handling',
        fieldName: 'action',
        minWidth: 160,
        maxWidth: 200,
        isResizable: true,
        filter: true
      },
      {
        key: 'result',
        name: 'Resultat',
        fieldName: 'result',
        minWidth: 200,
        maxWidth: 350,
        isResizable: true,
        onRender: (item: IAuditLog) => <span style={{ color: item.success ? undefined : "red" }} title={item.result}>{item.result ?? (item.success ? "" : `Fejl (${item.statusCode})`)}</span>
      },
      {
        key: 'durationMs',
        name: 'Varighed (ms)',
        fieldName: 'durationMs',
        minWidth: 90,
        maxWidth: 90,
        isResizable: false
      }
    ] as IFilterableColumn[];

    React.useEffect(() => {
      if(isAdministrator)
        Fetch(periodDays);
    }, [periodDays]);

    const Fetch = async (days: number) => {
      setAllEntries(undefined);
      setErrorText('');

      const to = new Date();
      Services?.AuditLog.GetAll(subDays(to, days), to).then((result: IAuditLog[]) => {
        setAllEntries(result);
      }).catch(() => {
        setAllEntries([]);
        setErrorText('Audit log kunne ikke hentes');
      });
    }

    const matchesSearch = (i: IAuditLog, term: string) : boolean => {
      return formatTimestamp(i.created).indexOf(term) > -1 ||
        (i.userName + "").toLowerCase().indexOf(term) > -1 ||
        (i.userEmail + "").toLowerCase().indexOf(term) > -1 ||
        (i.guildShortName + "").toLowerCase().indexOf(term) > -1 ||
        (i.area + "").toLowerCase().indexOf(term) > -1 ||
        (i.action + "").toLowerCase().indexOf(term) > -1 ||
        (i.entityId + "").toLowerCase().indexOf(term) > -1 ||
        (i.result + "").toLowerCase().indexOf(term) > -1;
    }

    const entries = React.useMemo(() : IAuditLog[] | undefined => {
      if(allEntries === undefined) return undefined;

      const term = searchTerm.toLocaleLowerCase();
      return term === "" ? allEntries : allEntries.filter(i => matchesSearch(i, term));
    }, [allEntries, searchTerm]);

    const COMMANDS: ICommandBarItemProps[] = [
      {
        key: 'period',
        text: PERIODS.find(p => p.days === periodDays)?.text,
        iconProps: { iconName: 'Calendar' },
        subMenuProps: {
          items: PERIODS.map(p => ({
            key: `period${p.days}`,
            text: p.text,
            checked: p.days === periodDays,
            onClick: () => { setPeriodDays(p.days); }
          }))
        }
      }
    ];

    const CreateFarMenuItems: ICommandBarItemProps[] = [
      {
        key: "search",
        onRender: () => <SearchBox
          placeholder="Søg efter ..."
          className={appstyles.searchBox}
          value={searchTerm}
          onSearch={(newValue) => { setSearchTerm(newValue ?? ""); } }
          onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => { setSearchTerm(newValue ?? ""); }}
          onClear={ev => { setSearchTerm(""); }}
          />
      }
    ];

    const DetailField = (fieldProps: { label: string, value?: string | number }) => {
      return <Stack>
        <Label>{fieldProps.label}</Label>
        <Text>{fieldProps.value ?? "-"}</Text>
      </Stack>
    }

    if(!isAdministrator)
      return <MessageBar messageBarType={MessageBarType.blocked}>Du har ikke adgang til audit log.</MessageBar>

    return <div className={appstyles.contentContainer}>
      {errorText && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorText('')}>{errorText}</MessageBar>}
      <ListComponent commands={COMMANDS} farItems={CreateFarMenuItems} title={`Audit log (${visibleCount})`}>
        <FreezableDetailsList
          items={entries as IAuditLog[]}
          columns={COLUMNS}
          setKey="keyAuditLog"
          selectionMode={SelectionMode.single}
          enableClickRowOpen={true}
          onFilteredItemsChanged={(items) => { setVisibleCount(items?.length ?? 0); }}
          onSelectionChanged={(item: any) => {
            if(item !== undefined)
              setSelected(item);
          }}
        />
      </ListComponent>
      {selected &&
        <Panel
          headerText={`${selected.action} - ${selected.area}`}
          isOpen={true}
          isLightDismiss={true}
          onDismiss={() => { setSelected(undefined); }}
          closeButtonAriaLabel="Luk panel"
          type={PanelType.medium}>
          <Stack tokens={{ childrenGap: 10 }}>
            <DetailField label="Tidspunkt" value={formatTimestamp(selected.created)} />
            <DetailField label="Bruger" value={selected.userName ? `${selected.userName} (${selected.userEmail})` : selected.userEmail} />
            <DetailField label="Laug" value={selected.guildShortName} />
            <DetailField label="Id" value={selected.entityId} />
            <DetailField label="Kald" value={`${selected.httpMethod} ${selected.path}`} />
            <Stack>
              <Label>Resultat</Label>
              <Text style={{ color: selected.success ? undefined : "red" }}>{`${selected.statusCode ?? ""} ${selected.result ?? ""}`}</Text>
            </Stack>
            <DetailField label="Varighed (ms)" value={selected.durationMs} />
            <DetailField label="IP-adresse" value={selected.ipAddress} />
            <DetailField label="Browser" value={selected.userAgent} />
            <Stack>
              <Label>Data</Label>
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, margin: 0 }}>{formatPayload(selected.payload) || "-"}</pre>
            </Stack>
          </Stack>
        </Panel>
      }
    </div>
}

export default AuditLog;
